import { createFileRoute } from "@tanstack/react-router";
import { HostingSalesPage } from "@/components/site/hosting-sales-page";
import { HostingQuoteDialog } from "@/components/site/hosting-quote-dialog";
import { getHostingPageMeta } from "@/lib/hosting-public.functions";

const SLUG = "hosting/dedicated-servers";
const CANONICAL = "/hosting/dedicated-servers";
const FALLBACK_TITLE = "Dedicated Servers — Bare Metal, GPU & Storage";
const FALLBACK_DESC = "Enterprise dedicated servers with AMD EPYC & Intel Xeon, NVMe RAID, up to 100 Gbps uplinks, DDoS protection, IPMI and 24/7 support.";

export const Route = createFileRoute("/hosting/dedicated-servers/")({
  component: DedicatedIndex,
  loader: () => getHostingPageMeta({ data: { slug: SLUG } }).catch(() => null),
  head: ({ loaderData }) => {
    const meta = loaderData ?? null;
    const title = meta?.seo_title || meta?.title || FALLBACK_TITLE;
    const desc = meta?.seo_description || meta?.subtitle || FALLBACK_DESC;
    const image = meta?.og_image_url || meta?.hero_image_url || undefined;
    const tags: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (image) {
      tags.push({ property: "og:image", content: image });
      tags.push({ name: "twitter:image", content: image });
    }
    return { meta: tags, links: [{ rel: "canonical", href: CANONICAL }] };
  },
});

function DedicatedIndex() {
  return (
    <>
      <HostingSalesPage slug={SLUG} />
      <section className="border-t border-border bg-gradient-to-b from-background to-muted/30 py-16">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Need a custom build?</h2>
          <p className="text-sm text-muted-foreground sm:text-base">
            Multi-node clusters, GPU pods, private VLANs, colocation — tell us what you need and our
            infrastructure team will scope a tailored quote within one business day.
          </p>
          <HostingQuoteDialog slug={SLUG} />
        </div>
      </section>
    </>
  );
}
